import { useState } from 'react';
import { Button, Grid, Link, Typography, Divider } from '@mui/material';
import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { apiConstants, postMultipart, getGeneric } from '../../common/constants';
import ExtraDocsEditor, { collectExtraDocs, categoryLabel } from './ExtraDocsEditor';

const FIXED = [
    { key: 'invoice', label: 'Invoice', docType: 'INVOICE' },
    { key: 'eway_bill', label: 'E-Way Bill', docType: 'EWAY_BILL' },
    { key: 'po_copy', label: 'PO Copy', docType: 'PO_COPY' },
];

/**
 * Unified document-upload popup for a B2B shipment. Holds the 3 fixed docs
 * (Invoice / E-Way Bill / PO Copy) + categorized extra docs, with NO text-field
 * requirements. Picks the endpoint by context:
 *   - ABD (active appointment present) -> appointment-specific add_documents (fixed = set, extras = appended)
 *   - non-ABD                          -> /additional_documents/upload (no PO)
 * Reused (ported verbatim) in the merchant panel.
 */
export default function DocumentsModal({ rowData, setOpen, setReload }) {
    const { enqueueSnackbar } = useSnackbar();
    const [submitting, setSubmitting] = useState(false);
    const [files, setFiles] = useState({ invoice: null, eway_bill: null, po_copy: null });
    const [extraDocRows, setExtraDocRows] = useState([]);

    const awb = rowData?.awb_number;
    const isABD = !!(rowData?.appointmentDelivery && rowData?.appointmentDeliveryData);
    const ad = isABD ? rowData.appointmentDeliveryData : rowData?.additionalDocuments;
    const docUrlApi = isABD ? apiConstants.APPOINTMENT_DELIVERY_DOC_URL : apiConstants.ADDITIONAL_DOCUMENTS_DOC_URL;

    const fixedKey = (docType) => {
        if (!ad) return null;
        if (isABD) {
            return docType === 'INVOICE' ? ad.appointment_del_invoice_s3_key
                : docType === 'EWAY_BILL' ? ad.appointment_del_eway_bill_s3_key
                    : ad.appointment_del_po_copy_s3_key;
        }
        return docType === 'INVOICE' ? ad.invoiceS3Key
            : docType === 'EWAY_BILL' ? ad.ewayBillS3Key
                : ad.poCopyS3Key;
    };
    const existingExtra = (isABD ? ad?.appointment_del_extra_docs : ad?.extraDocs) || [];

    const openUrl = (params) => async () => {
        try {
            const res = await getGeneric(docUrlApi, { awbNumber: awb, ...params });
            const url = res?.data?.responseObj?.url || res?.data?.url;
            if (url) window.open(url, '_blank');
            else enqueueSnackbar('Document URL missing', { variant: 'warning' });
        } catch (e) {
            enqueueSnackbar(e?.response?.data?.message || 'Failed to fetch document', { variant: 'error' });
        }
    };
    const viewExtra = (doc) => openUrl({ docId: doc?.docId })();

    const onSubmit = async () => {
        setSubmitting(true);
        try {
            const fd = new FormData();
            const { extraDocs: newMeta, error } = collectExtraDocs(extraDocRows, fd);
            if (error) {
                enqueueSnackbar(error, { variant: 'error' });
                setSubmitting(false);
                return;
            }
            const anyFixed = files.invoice || files.eway_bill || files.po_copy;
            if (!anyFixed && newMeta.length === 0) {
                enqueueSnackbar('Add at least one document.', { variant: 'warning' });
                setSubmitting(false);
                return;
            }
            if (files.invoice) fd.append('invoice', files.invoice);
            if (files.eway_bill) fd.append('eway_bill', files.eway_bill);
            if (files.po_copy) fd.append('po_copy', files.po_copy);

            let payload;
            let api;
            if (isABD) {
                // Appointment add_documents: fixed docs are set; extras are appended (existing preserved server-side).
                payload = { awbNumber: awb, newDocs: newMeta };
                api = apiConstants.APPOINTMENT_DELIVERY_ADD_DOCS;
            } else {
                // Non-ABD upload (no PO): the endpoint replaces the extraDocs list, so send existing + new.
                payload = { awbNumber: awb, extraDocs: [...existingExtra, ...newMeta] };
                api = apiConstants.ADDITIONAL_DOCUMENTS_UPLOAD;
            }
            fd.append('data', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
            await postMultipart(api, fd);
            enqueueSnackbar('Documents saved', { variant: 'success' });
            setOpen(false);
            setReload?.((r) => r + 1);
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || 'Something went wrong', { variant: 'error' });
        }
        setSubmitting(false);
    };

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {isABD ? 'Documents attach to the current appointment.' : 'Upload documents for this shipment.'}
            </Typography>
            <Grid container spacing={2}>
                {FIXED.map(({ key, label, docType }) => {
                    const existingKey = fixedKey(docType);
                    const selected = files[key];
                    return (
                        <Grid item xs={12} sm={4} key={key}>
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, height: '100%' }}>
                                <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>{label}</Typography>
                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    <Button variant="outlined" size="small" component="label">
                                        {selected || existingKey ? 'Replace' : 'Choose File'}
                                        <input type="file" hidden onChange={(e) => setFiles((p) => ({ ...p, [key]: e.target.files?.[0] || null }))} />
                                    </Button>
                                    {!selected && existingKey && (
                                        <Link component="button" type="button" variant="caption" onClick={openUrl({ docType })}>View</Link>
                                    )}
                                </Box>
                                {selected && (
                                    <Typography variant="caption" noWrap display="block" title={selected.name} sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {selected.name}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>
            <Divider sx={{ my: 2 }} />
            {existingExtra.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        Existing additional documents
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                        {existingExtra.map((xd) => (
                            <Button key={xd.docId} size="small" variant="text" onClick={() => viewExtra(xd)}>
                                {xd.docLabel || categoryLabel(xd.docType)}
                            </Button>
                        ))}
                    </Box>
                </Box>
            )}
            <ExtraDocsEditor rows={extraDocRows} setRows={setExtraDocRows} onView={viewExtra} existingCount={existingExtra.length} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                <Button variant="outlined" size="small" onClick={() => setOpen(false)}>Close</Button>
                <LoadingButton loading={submitting} variant="contained" size="small" onClick={onSubmit}>Save Documents</LoadingButton>
            </Box>
        </Box>
    );
}
